import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/Button/Button';
import Input from '../../../components/Input/Input';
import Select from '../../../components/Select/Select';
import PermissionManager from './PermissionOption/PermissionManager';
import Modal from '../../../components/Modal/Modal';
import './NewRole.css';
//services
import { role } from '../../../services/role';
import { usersList } from '../../../services/user';

const NewRole = () => {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState({ text: '', highlight: '' });
  const [modalMessage, setModalMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    users: [],
    description: '',
    permissions: {}
  });
  const [users, setUsers] = useState([]);
  const [modulesList, setModulesList] = useState([]);

  useEffect(() => {
    fetchPermissions();
    fetchUsers();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await role.rolesWithPermissions();
      if (response.status === 'success' && Array.isArray(response.data)) {
        const groupedModules = groupModules(response.data);
        setModulesList(groupedModules);
        
        // Inicializar el array de módulos con sus permisos
        const initialPermissions = [];
        groupedModules.forEach(module => {
          if (Array.isArray(module.children)) {
            module.children.forEach(child => {
              // Crear un objeto para cada child (módulo)
              initialPermissions.push({
                module_id: child.id,
                permissions: []
              });
            });
          }
        });

        // Actualizar formData con los permisos inicializados
        setFormData(prev => ({
          ...prev,
          permissions: initialPermissions
        }));
      } else {
        console.error('Permissions data is not in expected format');
        setModulesList([]);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersList.getDataUser({page: 1, items_per_page: 100});
      if (response.status === 'success') {
        const users = response.data.data.map(user => ({
          value: user.id,
          label: user.name
        }));
        setUsers(users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  function groupModules(data) {
    // 1. Separar los elementos principales (que tienen 'management') y los demás
    const mainModules = data.filter(item => item.name.includes('_management'));
    const otherModules = data.filter(item => !item.name.includes('_management'));
  
    // 2. Crear la estructura agrupada
    const groupedData = mainModules.map(mainModule => {
      const prefix = mainModule.name.split('_')[0];
      
      // Encontrar todos los módulos que comienzan con el mismo prefijo (incluyendo al padre)
      const allRelatedModules = data.filter(item => 
        item.name.startsWith(prefix + '_')
      );
  
      // Clonar el padre sin permisos para el nivel superior
      const parentWithoutPermissions = {
        ...mainModule,
        permissions: undefined // Eliminamos los permisos del nivel padre
      };
  
      // Para los hijos, mantenemos todos los módulos relacionados (incluyendo al padre completo)
      const children = allRelatedModules.map(module => ({
        ...module,
        // Marcamos si es el padre para referencia
        isParent: module.id === mainModule.id
      }));
  
      return {
        ...parentWithoutPermissions,
        children: children.length > 0 ? children : undefined
      };
    });
  
    // 3. Añadir módulos que no tienen un padre 'management'
    const remainingModules = otherModules.filter(child => 
      !groupedData.some(group => 
        group.children && group.children.some(c => c.id === child.id)
      )
    ).map(item => ({ ...item, permissions: undefined })); // También eliminamos permisos de estos
  
    return [...groupedData, ...remainingModules];
  }

  const handleInputChange = (e, field) => {
    // For React-Select components, e is the selected option(s)
    // For regular inputs, e is the event object
    const isReactSelect = !e.target;
    const name = field || (isReactSelect ? 'users' : e.target?.name);
    let value;

    if (isReactSelect) {
      // Handle multiple selection
      value = Array.isArray(e) ? e : [e].filter(Boolean);
    } else {
      value = e.target.value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionChange = (section, permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [section]: {
          ...prev.permissions[section],
          [permission]: !prev.permissions[section][permission]
        }
      }
    }));
  };

  const handleCancel = () => {
    if (error) {
      setError(false);
      setIsModalOpen(false);
      return;
    }
    navigate('/dashboard/roles');
  };

  const validateField = (value, fieldName) => {

    // Para campos de texto
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return `${fieldName} is required`;
    }
    if (typeof value === 'string') {
      if (value.length < 3) {
        return `${fieldName} must be at least 3 characters long`;
      }
      if (value.length > 250) {
        return `${fieldName} must not exceed 250 characters`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    // Validar campos
    const nameError = validateField(formData.name, 'Name');
    const descriptionError = validateField(formData.description, 'Description');

    if (nameError || descriptionError ) {
      setModalTitle({ text: 'Validation', highlight: 'Error' });
      setModalMessage(nameError || descriptionError );
      setError(true);
      setIsModalOpen(true);
      return;
    }

    // Preparar los datos para enviar
    const dataToSend = {
      name: formData.name,
      description: formData.description,
      user: formData.user?.value || formData.user,
      permissions: formData.permissions
    };

    // Filtrar solo los módulos que tienen permisos seleccionados
    const modulesWithPermissions = formData.permissions
      .filter(module => module.permissions && module.permissions.length > 0)
      .map(module => ({
        module_id: module.module_id,
        permissions: module.permissions
      }));

    const data = {
      name: formData.name,
      description: formData.description,
      modules: modulesWithPermissions
    };
    console.log('Data to save:', data);
    try {
      const response = await role.createRole(data);
      if (response.status === 'success') {
        const roleId = response.data.id;
        if(formData.users && formData.users.length > 0){
          formData.users.forEach(user => {
            setRoleToUser(roleId, user.value);
          });
        }
        setModalTitle({ text: 'Role', highlight: 'Created' });
        setModalMessage('The role has been created successfully');
        setError(false);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('Error creating role:', error);
      setModalTitle({ text: 'Error', highlight: 'Creating Role' });
      setModalMessage(error.response?.data?.message || 'There was an error creating the role');
      setError(true);
      setIsModalOpen(true);
    }
  };

  const setRoleToUser = async (roleId, userId) => {
    try {
      const dataToSend = {
        id: userId,
        role_id: roleId
      };
      console.log('Data to save:', dataToSend);
      const response = await usersList.updateUser(dataToSend);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  return (
    <div className="new-role-page">
      <div className="new-role-container">
        <h1 className="page-title">
          <span className="highlight">Insert</span> the following data
        </h1>

        <div className="form-section">
          <div className="input-group-roles">
            <div className="form-field-roles">
              <label>Name of role*</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange({ target: { name: 'name', value: e.target.value } })}
                placeholder="Enter the name of the role"
                required
                size="small"
                error={errors.name}
              />
            </div>
            <div className="form-field-roles">
              <label>User</label>
              <Select
                value={formData.users}
                onChange={(e) => handleInputChange(e, 'users')}
                options={users}
                placeholder="Search for users..."
                name="users"
                isMulti={true}
              />
            </div>
            <div className="form-field-roles">
              <label>Role description*</label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange({ target: { name: 'description', value: e.target.value } })}
                placeholder="Enter description"
                required
                size="small"
                error={errors.description}
              />
            </div>
          </div>

          <PermissionManager
            permissions={modulesList}
            currentValues={formData.permissions}
            onChange={(newValues) => setFormData(prev => ({
              ...prev,
              permissions: newValues
            }))}
          />
        </div>

        <div className="form-actions-roles">
          <Button
            onClick={handleCancel}
            variant="warning"
            fullWidth
            borderRadius={20}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            fullWidth
            borderRadius={20}
          >
            Save
          </Button>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        error={error}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        onConfirm={handleCancel}
      />
    </div>
  );
};

export default NewRole;